import { createRouter, createWebHistory } from 'vue-router'
import store from '@/store/index.js'
import { h, resolveComponent } from "vue";

const routes = [
  {
    path: '/login',
    component: () => import('@/views/templates/pages/CenterLayout'),
    beforeEnter: async (to) => {
      localStorage.removeItem('managementCompanyCode');
      let mccd = typeof to.query.mccd === 'string'
        ? to.query.mccd.trim()
        : '';
      // redirect内にmccdが含まれる場合に抽出
      if (!mccd && typeof to.query.redirect === 'string') {
        const queryString = to.query.redirect.split('?')[1] ?? '';
        mccd = new URLSearchParams(queryString).get('mccd')?.trim() ?? '';
      }
      // mccdがない、または空の場合
      if (!mccd) {
        return { name: 'ErrorInaccessible', replace: true };
      }
      try {
        const response = await store.dispatch('login_form/load', { mccd });
        // 存在しない管理会社コード
        if (!response) {
          return { name: 'ErrorInaccessible', replace: true };
        }
        // 存在する管理会社コード
        localStorage.setItem('managementCompanyCode', mccd);
        return true;
      } catch {
        return { name: 'ErrorInaccessible', replace: true };
      }
    },
    children: [
      {
        path: '',
        name: 'Login',
        meta: {
          title: 'ログイン',
          noIndex: true,
        },
        component: () => import('@/views/pages/Login'),
      },
    ]
  },
  {
    path: '/forgot-password',
    component: () => import('@/views/templates/pages/UnauthorizedLayout'),
    children: [
      {
        path: '',
        name: 'ForgotPassword',
        meta: {
          title: 'ログインにお困りの方'
        },
        component: () => import('@/views/pages/ForgotPassword'),
      }
    ]
  },
  {
    path: '/faq',
    component: () => import('@/views/templates/pages/SimpleLayout'),
    children: [
      {
        path: '',
        name: 'Faq',
        meta: {
          title: 'FAQ',
        },
        component: () => import('@/views/pages/ZendeskLogin')
      },
    ]
  },
  {
    path: '/registration',
    name: 'Registration',
    component: () => import('@/views/templates/pages/UnauthorizedLayout'),
    children: [
      {
        path: 'privacy-policy',
        name: 'PrivacyPolicyAgreement',
        meta: {
          title: '個人情報の取り扱いについて'
        },
        component: () => import('@/views/pages/Registration/PrivacyPolicyAgreement')
      },
      {
        path: 'guest',
        name: 'GuestRegister',
        meta: {
          title: '必要事項入力'
        },
        component: () => import('@/views/pages/Registration/GuestRegister')
      },
      {
        path: 'owner',
        name: 'OwnerRegister',
        meta: {
          title: '必要事項入力'
        },
        component: () => import('@/views/pages/Registration/OwnerRegister')
      },
      {
        path: 'guest-complete',
        name: 'GuestRegisterComplete',
        meta: {
          title: '必要事項入力完了'
        },
        component: () => import('@/views/pages/Registration/GuestRegisterComplete')
      },
      {
        path: 'send-mail',
        name: 'EmailRegister',
        meta: {
          title: 'アカウント登録'
        },
        component: () => import('@/views/pages/Registration/EmailRegister')
      },
      {
        path: 'company',
        name: 'Register',
        meta: {
          title: '仲介業者登録申請'
        },
        component: () => import('@/views/pages/Registration/Register'),
      },
      {
        path: 'employee',
        name: 'InvitedBrokerRegister',
        meta: {
          title: 'アカウント登録'
        },
        component: () => import('@/views/pages/Registration/InvitedRegister'),
      },
    ]
  },
  {
    path: '/',
    component: () => import('@/views/templates/pages/DefaultLayout'),
    beforeEnter: (to, from, next) => {
      if (store.state.account.is_guest) {
        return store.dispatch('account/logoutGuest').then(() => {
          store.commit('account/resetData');
          next({ name: 'Login', query: {redirect: to.fullPath}});
        });
      }
      next();
    },
    meta: { requiresAuth: true },
    children: [
      {
        path: '/',
        name: 'Top',
        meta: {
          title: 'TOP',
          group: 'Home',
        },
        component: () => import('@/views/pages/Top'),
      },
      {
        path: 'account/view',
        name: 'AccountViewer',
        meta: {
          title: 'アカウント詳細',
          group: 'Account',
        },
        component: () => import('@/views/pages/Account/Viewer'),
      },
      {
        path: 'account/edit',
        name: 'AccountEditor',
        meta: {
          title: 'アカウント編集',
          group: 'Account',
        },
        component: () => import('@/views/pages/Account/Editor'),
      },
      {
        path: 'company',
        name: 'BrokerageCompany',
        component: {
          render() {
            return h(resolveComponent('router-view'))
          }
        },
        children: [
          {
            path: 'view',
            name: 'BrokerageCompanyViewer',
            meta: {
              title: '会社情報詳細',
              group: 'Company',
            },
            component: () => import('@/views/pages/BrokerageCompany/Viewer')
          },
          {
            path: 'edit',
            name: 'BrokerageCompanyEditor',
            meta: {
              title: '会社情報編集',
              group: 'Company',
            },
            component: () => import('@/views/pages/BrokerageCompany/Editor')
          }
        ]
      },
      {
        path: 'employee',
        name: 'Broker',
        component: {
          render() {
            return h(resolveComponent('router-view'))
          }
        },
        redirect: { name: 'BrokerList' },
        children: [
          {
            path: '',
            name: 'BrokerList',
            meta: {
              title: '社員一覧',
              group: 'Company',
            },
            component: () => import('@/views/pages/Broker/List')
          },
          {
            path: 'view/:id',
            name: 'BrokerViewer',
            meta: {
              title: '社員詳細',
              group: 'Company',
            },
            component: () => import('@/views/pages/Broker/Viewer')
          },
          {
            path: 'edit/:id',
            name: 'BrokerEditor',
            meta: {
              title: '社員編集',
              group: 'Company',
            },
            component: () => import('@/views/pages/Broker/Editor')
          },
          {
            path: 'invite',
            name: 'BrokerInvite',
            meta: {
              title: '社員招待',
              group: 'Company',
            },
            component: () => import('@/views/pages/Broker/Invite')
          }
        ]
      },
      {
        path: 'registration/merger-request',
        name: 'MergerRequestRegister',
        meta: {
          title: '企業統合依頼',
          group: 'Company',
        },
        component: () => import('@/views/pages/MergerRequest/Register')
      },
      {
        path: 'merger-request/:id',
        name: 'MergerRequestForm',
        meta: {
          title: '企業統合許諾',
          group: 'Company',
        },
        component: () => import('@/views/pages/MergerRequest/Viewer')
      },
      {
        path: 'issuance-request',
        name: 'Issuance',
        component: {
          render() {
            return h(resolveComponent('router-view'))
          }
        },
        redirect: { 'name': 'IssuanceRequestList' },
        children: [
          {
            path: '',
            name: 'IssuanceRequestList',
            meta: {
              title: '発行依頼履歴一覧',
              group: 'IssuanceHistory',
            },
            component: () => import('@/views/pages/IssuanceRequest/List')
          },
          {
            path: 'own',
            name: 'IssuanceRequestOwnList',
            meta: {
              title: '発行依頼履歴一覧（個人）',
              group: 'IssuanceHistory',
            },
            component: () => import('@/views/pages/IssuanceRequest/OwnList')
          },
          {
            path: 'view/:id',
            name: 'IssuanceRequestViewer',
            meta: {
              title: '発行依頼詳細',
              group: 'IssuanceHistory',
            },
            component: () => import('@/views/pages/IssuanceRequest/Viewer')
          },
          {
            path: 'own/view/:id',
            name: 'IssuanceRequestOwnViewer',
            meta: {
              title: '発行依頼詳細（個人）',
              group: 'IssuanceHistory',
            },
            component: () => import('@/views/pages/IssuanceRequest/OwnViewer')
          },
          {
            path: 'request',
            name: 'IssuanceRequestRegister',
            meta: {
              title: '発行依頼',
              group: 'IssuanceRequest',
            },
            component: () => import('@/views/pages/IssuanceRequest/Register')
          },
          {
            path: 'request-confirm',
            name: 'IssuanceRequestRegisterConfirm',
            meta: {
              title: '発行依頼確認',
              group: 'IssuanceRequest',
            },
            component: () => import('@/views/pages/IssuanceRequest/RegisterConfirm')
          },
          {
            path: 'request-accept',
            name: 'IssuanceRequestRegisterAccepted',
            meta: {
              title: '発行依頼受付完了',
              group: 'IssuanceRequest',
            },
            component: () => import('@/views/pages/IssuanceRequest/RegisterAccepted')
          },
          {
            path: 'edit/:id',
            name: 'IssuanceRequestEditor',
            meta: {
              title: '発行依頼修正',
              group: 'IssuanceRequest',
            },
            component: () => import('@/views/pages/IssuanceRequest/Editor')
          },
          {
            path: 'edit-confirm/:id',
            name: 'IssuanceRequestEditorConfirm',
            meta: {
              title: '発行依頼修正確認',
              group: 'IssuanceRequest',
            },
            component: () => import('@/views/pages/IssuanceRequest/EditorConfirm')
          },
          {
            path: 'edit-accept/:id',
            name: 'IssuanceRequestEditorAccepted',
            meta: {
              title: '発行依頼修正完了',
              group: 'IssuanceRequest',
            },
            component: () => import('@/views/pages/IssuanceRequest/EditorAccepted')
          },
          {
            path: 'transfer-statement-upload/:id',
            name: 'IssuanceRequestUploadTransferStatement',
            meta: {
              title: '振込明細書アップロード',
              group: 'IssuanceHistory',
            },
            component: () => import('@/views/pages/IssuanceRequest/UploadTransferStatement')
          },
          {
            path: 'request-complete',
            name: 'IssuanceRequestComplete',
            meta: {
              title: '発行依頼完了',
              group: 'IssuanceRequest',
            },
            component: () => import('@/views/pages/IssuanceRequest/Complete')
          },
          {
            path: 'download/:id',
            name: 'IssuanceRequestDownload',
            meta: {
              title: '発行物ダウンロード',
              group: 'IssuanceHistory',
            },
            component: () => import('@/views/pages/IssuanceRequest/Download')
          },
        ]
      },
      {
        path: 'reissuance-request',
        name: 'Reissuance',
        component: {
          render() {
            return h(resolveComponent('router-view'))
          }
        },
        redirect: { 'name': 'IssuanceRequestList' },
        children: [
          {
            path: 'request/:id',
            name: 'ReissuanceRequestRegister',
            meta: {
              title: '再発行依頼',
              group: 'IssuanceRequest',
            },
            component: () => import('@/views/pages/ReissuanceRequest/Register')
          },
          {
            path: 'request-confirm/:id',
            name: 'ReissuanceRequestRegisterConfirm',
            meta: {
              title: '再発行依頼確認',
              group: 'IssuanceRequest',
            },
            component: () => import('@/views/pages/ReissuanceRequest/RegisterConfirm')
          },
          {
            path: 'request-accept/:id',
            name: 'ReissuanceRequestRegisterAccepted',
            meta: {
              title: '再発行依頼受付完了',
              group: 'IssuanceRequest',
            },
            component: () => import('@/views/pages/ReissuanceRequest/RegisterAccepted')
          }
        ]
      },
      {
        path: 'document_management',
        name: 'PublicDocument',
        component: {
          render() {
            return h(resolveComponent('router-view'))
          }
        },
        redirect: { name: 'PublicDocumentList' },
        children: [
          {
            path: '',
            name: 'PublicDocumentList',
            meta: {
              title: '資料一覧',
              group: 'PublicDocument',
            },
            component: () => import('@/views/pages/PublicDocument/List')
          },
          {
            path: 'view/:id',
            name: 'PublicDocumentViewer',
            meta: {
              title: '資料詳細',
              group: 'PublicDocument',
            },
            component: () => import('@/views/pages/PublicDocument/Viewer')
          },
        ]
      },
      {
        path: 'merger/request-confirm'
        //todo
      },
      {
        path: 'notification/view/:id',
        name: 'NotificationViewer',
        meta: {
          title: 'お知らせ詳細',
          group: 'Home',
        },
        component: () => import('@/views/pages/Notification/Viewer'),
      },
      {
        path: 'operation-log',
        name: 'operationlog',
        component: {
          render() {
            return h(resolveComponent('router-view'));
          }
        },
        redirect: { 'name': 'OperationLogList' },
        children: [
          {
            path: '',
            name: 'OperationLogList',
            meta: {
              title: '操作ログ一覧',
              group: 'OperationLog',
            },
            component: () => import('@/views/pages/OperationLog/List')
          },
        ]
      },
      {
        path: 'error',
        name: 'Error',
        component: {
          render() {
            return h(resolveComponent('router-view'))
          }
        },
        children: [
          {
            path: 'notfound',
            name: 'BrokerErrorNotFound',
            meta: {
              title: 'ページを表示できません'
            },
            component: () => import('@/views/pages/Error/NotFound')
          },
        ]
      },
    ]
  },
  {
    path: '/guest',
    name: 'Guest',
    component: () => import('@/views/templates/pages/GuestLayout'),
    beforeEnter: (to, from, next) => {
      document.title = (to.meta.title ? to.meta.title + ' | ' : '') + 'ネオス重調＠CITY ゲスト向けシステム';
      store.commit('setPageMeta', to.meta);
      if (to.query.access_key) {
        sessionStorage.setItem('accessKey', to.query.access_key);
        store.dispatch('account/authGuest', to.query).then(() => {
          // access_keyの無いURLにリダイレクト
          next({ name: to.name, params: to.params, replace: true });
        }).catch(() => {
          // 実質403だけど404でまるめる
          next({ name: 'ErrorNotFound', replace: true });
        });
      } else if (store.state.account.is_guest === false) {
        // 実質401だけど404でまるめる
        next({ name: 'ErrorNotFound', replace: true });
      } else {
        if (store.state.account.management_company_code) {
          localStorage.setItem('managementCompanyCode', store.state.account.management_company_code);
        } else {
          localStorage.removeItem('managementCompanyCode');
        }
        next();
      }
    },
    children: [
      {
        path: 'document_management',
        name: 'GuestPublicDocument',
        component: {
          render() {
            return h(resolveComponent('router-view'))
          }
        },
        redirect: { name: 'GuestPublicDocumentList' },
        children: [
          {
            path: '',
            name: 'GuestPublicDocumentList',
            meta: {
              title: '資料一覧',
              group: 'PublicDocument',
            },
            component: () => import('@/views/pages/PublicDocument/List')
          },
          {
            path: 'view/:id',
            name: 'GuestPublicDocumentViewer',
            meta: {
              title: '資料詳細',
              group: 'PublicDocument',
            },
            component: () => import('@/views/pages/PublicDocument/Viewer')
          },
        ]
      },
      {
        path: 'issuance-request',
        name: 'GuestIssuance',
        component: {
          render() {
            return h(resolveComponent('router-view'))
          }
        },
        children: [
          {
            path: 'view',
            name: 'GuestIssuanceRequestViewer',
            meta: {
              title: '発行依頼詳細',
              group: 'IssuanceRequest',
            },
            component: () => import('@/views/pages/IssuanceRequest/Viewer')
          },
          {
            path: 'request',
            name: 'GuestIssuanceRequestRegister',
            meta: {
              title: '発行依頼',
              group: 'IssuanceRequest',
            },
            component: () => import('@/views/pages/IssuanceRequest/Register')
          },
          {
            path: 'request-confirm',
            name: 'GuestIssuanceRequestRegisterConfirm',
            meta: {
              title: '発行依頼確認',
              group: 'IssuanceRequest',
            },
            component: () => import('@/views/pages/IssuanceRequest/RegisterConfirm')
          },
          {
            path: 'request-accept',
            name: 'GuestIssuanceRequestRegisterAccepted',
            meta: {
              title: '発行依頼受付完了',
              group: 'IssuanceRequest',
            },
            component: () => import('@/views/pages/IssuanceRequest/RegisterAccepted')
          },
          {
            path: 'transfer-statement-upload',
            name: 'GuestIssuanceRequestUploadTransferStatement',
            meta: {
              title: '振込明細書アップロード',
              group: 'IssuanceRequest',
            },
            component: () => import('@/views/pages/IssuanceRequest/UploadTransferStatement')
          },
          {
            path: 'request-complete',
            name: 'GuestIssuanceRequestComplete',
            meta: {
              title: '発行依頼完了',
              group: 'IssuanceRequest',
            },
            component: () => import('@/views/pages/IssuanceRequest/Complete')
          },
          {
            path: 'download',
            name: 'GuestIssuanceRequestDownload',
            meta: {
              title: '発行物ダウンロード',
              group: 'IssuanceHistory',
            },
            component: () => import('@/views/pages/IssuanceRequest/Download')
          },
          {
            path: 'edit',
            name: 'GuestIssuanceRequestEditor',
            meta: {
              title: '発行依頼修正',
              group: 'IssuanceRequest',
            },
            component: () => import('@/views/pages/IssuanceRequest/Editor')
          },
          {
            path: 'edit-confirm',
            name: 'GuestIssuanceRequestEditorConfirm',
            meta: {
              title: '発行依頼修正確認',
              group: 'IssuanceRequest',
            },
            component: () => import('@/views/pages/IssuanceRequest/EditorConfirm')
          },
          {
            path: 'edit-accept',
            name: 'GuestIssuanceRequestEditorAccepted',
            meta: {
              title: '発行依頼修正確認',
              group: 'IssuanceRequest',
            },
            component: () => import('@/views/pages/IssuanceRequest/EditorAccepted')
          },
        ],
      },
      {
        path: 'reissuance-request',
        name: 'GuestReissuance',
        component: {
          render() {
            return h(resolveComponent('router-view'))
          }
        },
        children: [
          {
            path: 'request',
            name: 'GuestReissuanceRequestRegister',
            meta: {
              title: '再発行依頼',
              group: 'IssuanceRequest',
            },
            component: () => import('@/views/pages/ReissuanceRequest/Register')
          },
          {
            path: 'request-confirm',
            name: 'GuestReissuanceRequestRegisterConfirm',
            meta: {
              title: '再発行依頼確認',
              group: 'IssuanceRequest',
            },
            component: () => import('@/views/pages/ReissuanceRequest/RegisterConfirm')
          },
          {
            path: 'request-accept',
            name: 'GuestReissuanceRequestRegisterAccepted',
            meta: {
              title: '再発行依頼受付完了',
              group: 'IssuanceRequest',
            },
            component: () => import('@/views/pages/ReissuanceRequest/RegisterAccepted')
          }
        ]
      },
      {
        path: 'error',
        name: 'GuestError',
        component: {
          render() {
            return h(resolveComponent('router-view'))
          }
        },
        children: [
          {
            path: 'notfound',
            name: 'BrokerErrorNotFound',
            meta: {
              title: 'ページを表示できません'
            },
            component: () => import('@/views/pages/Error/NotFound')
          },
        ]
      },
    ]
  },
  {
    path: '/',
    name: 'GeneralError',
    component: () => import('@/views/templates/pages/CenterLayout'),
    children: [
      {
        path: 'notfound',
        name: 'ErrorNotFound',
        meta: {
          title: '404 NotFound.'
        },
        component: () => import('@/views/pages/Error/NotFound')
      },
      {
        path: 'unauthenticated',
        name: 'ErrorUnauthenticated',
        meta: {
          title: '401 Unauthenticated.'
        },
        component: () => import('@/views/pages/Error/Unauthenticated')
      },
      {
        path: 'inaccessible',
        name: 'ErrorInaccessible',
        meta: {
          title: 'アクセスできません',
          noIndex: true,
        },
        component: () => import('@/views/pages/Error/Inaccessible')
      },
    ]
  },
  {
    path: '/receipt/',
    component: () => import('@/views/templates/pages/SimpleLayout'),
    children: [
      {
        path: 'issue/:id',
        name: 'ReceiptIssue',
        meta: {
          title: '領収書発行',
        },
        component: () => import('@/views/pages/IssuanceRequest/ReceiptIssue')
      },
    ]
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    // ページ遷移のタイミングで一律最上部にスクロール
    return { top: 0 };
  }
})

router.beforeEach((to, from, next) => {
  document.title = (to.meta.title ? to.meta.title + ' | ' : '') + 'ネオス重調＠CITY 仲介業者向けシステム';
  // 既存のrobots metaタグがある場合は削除
  const existingMeta = document.querySelector('meta[name="robots"]');
  if (existingMeta) {
    document.head.removeChild(existingMeta);
  }
  // メタ情報がnoIndexの場合にのみ、noindex metaタグを追加
  if (to.meta.noIndex) {
    const metaTag = document.createElement('meta');
    metaTag.setAttribute('name', 'robots');
    metaTag.setAttribute('content', 'noindex, nofollow');
    document.head.insertBefore(metaTag, document.head.firstChild); // headの先頭に挿入
  }
  store.commit('setPageMeta', to.meta)
  if (to.matched.some(record => record.meta.requiresAuth)) {
    // mccdを先に取得
    const managementCompanyCode = localStorage.getItem('managementCompanyCode');
    store.dispatch('account/refresh')
      .then(() => {
        store.commit('setOriginalRoute', { value: to })
        return next(); // 認証成功時の処理
      })
      .catch(() => {
        // 管理会社コードがある場合、セットしてログイン画面へ遷移させる
        if (managementCompanyCode) {
          // リダイレクト URL を作成
          const redirectUrl = to.fullPath; // 元のURL 
          const loginRoute = {
            name: 'Login',
            query: {
              redirect: redirectUrl,
              ...(managementCompanyCode ? { mccd: managementCompanyCode } : {}) // mccd を条件付きで追加
            }
          };
          // ログイン画面にリダイレクト
          return next(loginRoute);
        } else {
          store.commit('setOriginalRoute', { value: to })
          return next(); // 認証失敗時も1度だけ呼び出す
        }
      });
  } else {
    next();
  }
});

export default router
